/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable import/named */
/* eslint-disable no-plusplus */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-useless-return */
/* eslint-disable default-case */
/* eslint-disable lines-between-class-members */
import axios from "axios"
import HermesWebSDK, { types } from "lib/hermes-web-sdk"
import { actionCreator, store } from "store/store"
import { makeAutoObservable } from "mobx"
import { OrderNotification } from "order-manager/types/models/notification"
import { TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventCategory,
  HermesEventType,
  NotificationMethod,
} from "order-manager/constants/tracking"
import { getHermesUrl } from "constants/domain"
import { generateAxiosHeader } from "utils/api_helper"
import { getOrderManagerNotificationConfig } from "order-manager/utils/storage_config_helper"
import { CURRENT_TAB_KEY } from "constants/sessions_storage"
import { storageHelper } from "utils/storage"
import { attachInstrumentation } from "./helpers"

const { Topic, HermesError, ErrorCode } = types

export enum ConnectionStatus {
  Connecting,
  Connected,
  Disconnecting,
  Disconnected,
}

export default class HermesStore {
  static readonly componentName = "HermesStore"

  readonly hermesWebSDK: HermesWebSDK

  connectionStatus = ConnectionStatus.Disconnected

  isRetryFromMonitoring = false

  reconnectionFailed = false

  constructor() {
    makeAutoObservable(this, {
      hermesWebSDK: false,
      disconnect: false,
      _createHermesWebSDKConfig: false,
      _toWSSScheme: false,
      _init: false,
    })

    this.hermesWebSDK = new HermesWebSDK(this._createHermesWebSDKConfig())
    this._init()
  }

  connect = async (isRetryFromMonitoring: boolean = false) => {
    try {
      if (this.connectionStatus !== ConnectionStatus.Disconnected) {
        TroyWebTracker.sendEvent({
          category: EventCategory.hermesConnection,
          action: EventAction.createConnection,
          labels: {
            IS_CONNECTED: true,
          },
        })
        console.error("Cannot connect as the connection is not disconnected")
        return
      }

      this._setConnectionStatus(ConnectionStatus.Connecting)
      await this.hermesWebSDK.authenticate()
      this.hermesWebSDK.connect()
      this._updateNotificationConfig(true)
      /*
        We only count switching from monitoring method and reconnect to hermes is success
        The logic only goes to this part when this.hermesWebSDK.connect() don't trigger any exception
      */
      if (isRetryFromMonitoring) {
        this._setIsRetryFromMonitoring(true)
        TroyWebTracker.sendEvent({
          category: EventCategory.hermesConnection,
          action: EventAction.switchConnection,
          labels: {
            USED_METHOD: NotificationMethod.Hermes,
            FROM: "connect",
          },
        })
      }
    } catch (err) {
      // Safe to assume that we are disconnected when an error is received
      // because of the condition check above and assuming that no other code
      // had directly called the `connect` method on the SDK itself.
      // Only the `HermesStore` should be calling `this.hermesWebSDK.connect`.
      this._setConnectionStatus(ConnectionStatus.Disconnected)
      this._setIsRetryFromMonitoring(false)

      if (err instanceof HermesError) {
        switch (err.code) {
          case ErrorCode.Unauthorized: {
            TroyWebTracker.sendEvent({
              category: EventCategory.hermesConnection,
              action: EventAction.createConnection,
              labels: {
                ERROR: err,
                TYPE: HermesEventType.Unauthorized,
              },
            })
            return
          }
          case ErrorCode.Conflict: {
            TroyWebTracker.sendEvent({
              category: EventCategory.hermesConnection,
              action: EventAction.createConnection,
              labels: {
                ERROR: err,
                TYPE: HermesEventType.Conflict,
              },
            })

            this._updateNotificationConfig(false)
            /*
              We only count switching from first opened page to know how much user switched to fallback method
              Retry connection from monitoring interval will be retry every N minutes in it makes metric become not valid
            */
            if (!isRetryFromMonitoring) {
              TroyWebTracker.sendEvent({
                category: EventCategory.hermesConnection,
                action: EventAction.switchConnection,
                labels: {
                  USED_METHOD: NotificationMethod.Fallback,
                  FROM: "connect",
                },
              })
            }

            return
          }
          default: {
            TroyWebTracker.sendEvent({
              category: EventCategory.hermesConnection,
              action: EventAction.createConnection,
              labels: {
                ERROR: err,
                TYPE: HermesEventType.Unknown,
              },
            })
          }
        }
      }

      return
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.hermesConnection,
      action: EventAction.createConnection,
      labels: {
        SUCCESS: true,
      },
    })
  }

  disconnect = () => {
    try {
      this.hermesWebSDK.disconnect()
    } catch (err) {
      TroyWebTracker.sendEvent({
        category: EventCategory.hermesConnection,
        action: EventAction.closeConnection,
        labels: {
          ERROR: err,
          TYPE: HermesEventType.Unknown,
        },
      })

      return
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.hermesConnection,
      action: EventAction.closeConnection,
      labels: {
        SUCCESS: true,
        TYPE: HermesEventType.Disconnected,
      },
    })
  }

  _createHermesWebSDKConfig = () => {
    const axiosHeader = generateAxiosHeader()
    // Hermes only allow standard header plus Authorization
    // https://grab.slack.com/archives/C019Z3Y9739/p1666323682266399?thread_ts=1657609236.556239&cid=C019Z3Y9739
    delete axiosHeader.requestSource
    const httpClient = axios.create({
      baseURL: getHermesUrl(),
      timeout: 10000,
      headers: axiosHeader,
      withCredentials: true,
    })
    attachInstrumentation(httpClient, "HermesHTTPClient")

    return {
      httpClient,
      // logger: datadogLogger.asLogger(),
      webSocketURL: this._toWSSScheme(getHermesUrl()),
    }
  }

  _toWSSScheme = (hermesBaseURI: string) => {
    return hermesBaseURI.replace("https:", "wss:")
  }

  _init = () => {
    this.hermesWebSDK.subscribe(event => {
      switch (event?.topic) {
        // Hermes default topic handler
        case Topic.HermesWebSDKConnecting: {
          this._setConnectionStatus(ConnectionStatus.Connecting)
          return
        }
        case Topic.HermesWebSDKConnected: {
          this._setConnectionStatus(ConnectionStatus.Connected)
          this.reconnectionFailed = false
          return
        }
        case Topic.HermesWebSDKDisconnecting: {
          this._setConnectionStatus(ConnectionStatus.Disconnecting)
          return
        }
        case Topic.HermesWebSDKDisconnected:
        case Topic.HermesWebSDKConnectionError: {
          this._setConnectionStatus(ConnectionStatus.Disconnected)
          this._setIsRetryFromMonitoring(false)
          this._updateNotificationConfig(false)
          TroyWebTracker.sendEvent({
            category: EventCategory.hermesConnection,
            action: EventAction.switchConnection,
            labels: {
              USED_METHOD: NotificationMethod.Fallback,
              FROM: "force disconnect",
            },
          })
          return
        }
        case Topic.HermesWebSDKReconnectionFailure: {
          this.reconnectionFailed = true
          return
        }

        // Order manager notification topic handler
        case Topic.NewOrderNotification: {
          const newOrder: OrderNotification = event?.payload as OrderNotification
          newOrder.source = NotificationMethod.Hermes
          newOrder.isRetryFromMonitoring = this.isRetryFromMonitoring
          store.dispatch(
            actionCreator.orderManager.newOrderNotification({
              data: newOrder,
            }),
          )
          return
        }
      }
    })
  }

  _setConnectionStatus = (status: ConnectionStatus) => {
    this.connectionStatus = status
  }

  _setIsRetryFromMonitoring = (value: boolean) => {
    this.isRetryFromMonitoring = value
  }

  _updateNotificationConfig = (hermesConnectionSuccess: boolean) => {
    const notifConfig = getOrderManagerNotificationConfig()

    // Only selected tab can update the configurations
    if (
      notifConfig.activeTabID === "" ||
      sessionStorage.getItem(CURRENT_TAB_KEY) === notifConfig.activeTabID
    ) {
      notifConfig.enableFallback = !hermesConnectionSuccess
      notifConfig.enableHermes = hermesConnectionSuccess
      storageHelper.setOrderManagerNotificationConfig(notifConfig)
    }
  }
}
